import type { FC, ReactNode } from 'react'
import type { PhotoId } from '@/components/generals/photo'

import { cn } from '@/lib/utils'
import { delay } from '@/lib/motion'
import { Photo } from '@/components/generals/photo'
import { Wash } from '@/components/generals/backdrops'
import { TornPaper } from '@/components/generals/paper'
import { ButtonLink } from '@/components/generals/button-link'
import { MockScale } from '@/components/generals/mock-scale'

import {
    ColdInboxScene,
    SCENE_WIDTH,
    SpreadsheetScene,
    StalledRoleScene
} from '@/components/art/scenes'
import { routes } from '@/constants/site'

const SECTION = 'section-x relative isolate mx-auto max-w-[calc(1080px+2*clamp(16px,3vw,32px))]'

/* The scouts behind your introductions ---------------------------------------------- */

interface ClusterScout {
    photo: PhotoId
    name: string
    className: string
    rotate: string
}

/* Four scout photo prints overlapping in a loose two by two, the way they sit in the design */
const CLUSTER: ClusterScout[] = [
    { photo: 'elena', name: 'Maren Holt', className: 'left-0 top-0 w-[54%] z-[1]', rotate: '-4deg' },
    { photo: 'luca', name: 'Teodor Valk', className: 'left-[44%] top-[5%] w-[54%] z-[2]', rotate: '3deg' },
    { photo: 'sofia', name: 'Priya Nandakumar', className: 'left-[2%] top-[44%] w-[52%] z-[3]', rotate: '2deg' },
    { photo: 'mateo', name: 'Tomás Vidal', className: 'left-[42%] top-[42%] w-[54%] z-[4]', rotate: '-3deg' }
]

export const ScoutsBehind: FC = (): ReactNode => {
    return (
        <section id='scouts' aria-labelledby='scouts-title' className={cn(SECTION, 'pb-[90px] pt-[60px] max-[991px]:pb-[66px] max-sm:pb-14 max-sm:pt-11')}>
            <div className='reveal-group flex items-center justify-between gap-12 max-md:flex-col max-md:items-start'>
                <div className='rise'>
                    <h2 id='scouts-title' className='type-section max-w-[560px]'>
                        The scouts behind your introductions
                    </h2>
                    <p className='mt-7 max-w-[52ch] text-ink/80'>
                        They're design leaders with an eye for great work and deep ties to their communities. Each one brings a collective of designers whose work they know and respect
                    </p>
                </div>

                <div className='relative aspect-[393/381] w-[min(400px,42%)] shrink-0 max-[991px]:w-[min(340px,40%)] max-md:w-[min(340px,82%)]'>
                    {CLUSTER.map((scout, index) => (
                        <figure key={scout.name} className={cn('pop absolute', scout.className)} style={delay(200 + index * 140)}>
                            <div className='rounded-[6px] bg-paper p-[4%] shadow-[0_1px_2px_rgb(40_38_34/0.12),0_18px_32px_-16px_rgb(40_38_34/0.45)]' style={{ rotate: scout.rotate }}>
                                <Photo id={scout.photo} sizes='(min-width: 992px) 220px, 40vw' className='aspect-square w-full rounded-[3px]' />
                            </div>
                            <figcaption className='absolute bottom-[4%] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-paper px-2.5 py-1 text-[10.5px] text-ink/75 shadow-[0_0_0_1px_rgb(40_38_34/0.1),0_3px_8px_-4px_rgb(40_38_34/0.35)]'>
                                {scout.name}
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    )
}

/* How designers get scouted ---------------------------------------------------------- */

export const HowDesignersGetScouted: FC = (): ReactNode => {
    return (
        <section id='how-designers-get-scouted' aria-labelledby='scouted-title' className={cn(SECTION, 'py-[90px] text-center max-[991px]:pb-[66px] max-sm:py-14')}>
            <TornPaper className='reveal' surfaceClassName='scouted-field rounded-[5px]'>
                <div className='flex flex-col items-center px-[clamp(24px,4vw,64px)] py-[clamp(90px,10vw,150px)]'>
                    <h2 id='scouted-title' className='type-section'>
                        How designers get scouted
                    </h2>
                    <p className='mx-auto mt-5 max-w-[52ch] text-ink/80'>
                        Scouts invite designers whose work they rate into a private collective. Designers never apply and never pay. They only hear about roles worth their time
                    </p>
                    <ButtonLink href={routes.applyToScout} size='lg' className='mt-8 max-sm:h-10'>
                        Become a scout
                    </ButtonLink>
                </div>
            </TornPaper>
        </section>
    )
}

/* What we never do -------------------------------------------------------------------- */

interface Never {
    title: string
    line: string
    scene: ReactNode
}

const NEVERS: Never[] = [
    {
        title: 'Mass-message designers',
        line: 'Every intro is personal, from someone they respect',
        scene: <ColdInboxScene />
    },
    {
        title: 'Pass off a résumé dump as a shortlist',
        line: 'You get a handful, never a spreadsheet of names',
        scene: <SpreadsheetScene />
    },
    {
        title: "Take on roles we can't fill",
        line: "If we don't think we can fill yours, we'll tell you first",
        scene: <StalledRoleScene />
    }
]

/** Three pocket-size frayed sheets on a warm field, each holding a small scene of what we never do. */
export const WhatWeNeverDo: FC = (): ReactNode => {
    return (
        <section id='what-we-never-do' aria-labelledby='never-title' className={cn(SECTION, 'pb-[90px] pt-[72px] max-sm:pb-14')}>
            <Wash strength='light' />

            <h2 id='never-title' className='type-section reveal mb-10 max-w-[720px]'>
                What we never do
            </h2>

            <ul className='grid grid-cols-3 gap-6 max-[991px]:grid-cols-2 max-sm:grid-cols-1'>
                {NEVERS.map((item, index) => (
                    <li
                        key={item.title}
                        className={cn('reveal', index === 2 && 'max-[991px]:col-span-2 max-[991px]:w-full max-[991px]:max-w-[480px] max-[991px]:justify-self-center max-sm:col-span-1 max-sm:max-w-none')}
                        style={delay(index * 120)}
                    >
                        <TornPaper tone='sheet' className='h-full px-1 pb-[clamp(22px,2.2vw,30px)] pt-1'>
                            <MockScale designWidth={SCENE_WIDTH} className='mb-4 rounded-[8px]'>
                                {item.scene}
                            </MockScale>

                            <div className='mx-4 px-1 pt-0.5'>
                                <h3 className='type-card'>
                                    {item.title}
                                </h3>
                                <p className='mt-1.5 text-ink/80'>
                                    {item.line}
                                </p>
                            </div>
                        </TornPaper>
                    </li>
                ))}
            </ul>
        </section>
    )
}
